import React, { useState, useEffect } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { InputSwitch } from 'primereact/inputswitch';
import 'primereact/resources/primereact.min.css';
import 'primeicons/primeicons.css';
import 'primeflex/primeflex.css';
import { handleGetRequest } from '../../service/GetTemplate';
import { handlePutRequest } from '../../service/PutTemplate';
import { endPoints } from '../../Config';
import { useDispatch } from 'react-redux';
import ActiveInactiveDialog from '../../components/ActiveInactiveDialog';
import AddEditLevel2Question from './Components/AddEditLevel2Question';
import AddEditLevel3Question from './Components/AddEditLevel3Question';
import EditLevel1Question from './Components/EditLevel1Question';
import { handlePatchRequest } from '../../service/PatchTemplate';
import { Tag } from 'primereact/tag';

const Questions = () => {
    const [showL1Dialog, setShowL1Dialog] = useState(false);
    const [showL2Dialog, setShowL2Dialog] = useState(false);
    const [showL3Dialog, setShowL3Dialog] = useState(false);
    const [l1RowRecord, setL1RowRecord] = useState(null);
    const [l2RowRecord, setL2RowRecord] = useState(null);
    const [l3RowRecord, setL3RowRecord] = useState(null);
    const [l1Editable, setL1Editable] = useState(false);
    const [l2Editable, setL2Editable] = useState(false);
    const [l3Editable, setL3Editable] = useState(false);
    const [l1Viewable, setL1Viewable] = useState(false);
    const [l2Viewable, setL2Viewable] = useState(false);
    const [l3Viewable, setL3Viewable] = useState(false);
    const [level1Questions, setLevel1Questions] = useState([]);
    const [level2Questions, setLevel2Questions] = useState([]);
    const [level3Questions, setLevel3Questions] = useState([]);
    const [expandedRows, setExpandedRows] = useState({});
    const [expandedL2Rows, setExpandedL2Rows] = useState({});
    const [switchData, setSwitchData] = useState(null);
    const [switchLevel, setSwitchLevel] = useState(null); // 'L2' or 'L3'
    const [switchModal, setSwitchModal] = useState(false);
    // currentL1Parent stores Level 1 Mongo _id (used for fetching children)
    const [currentL1Parent, setCurrentL1Parent] = useState(null);
    // currentL1ParentId stores Level 1 numeric id (used as parentId when creating Level 2)
    const [currentL1ParentId, setCurrentL1ParentId] = useState(null);
    // currentL2Parent stores Level 2 Mongo _id (used for fetching children)
    const [currentL2Parent, setCurrentL2Parent] = useState(null);
    // currentL2ParentId stores Level 2 numeric id (used as parentId when creating Level 3)
    const [currentL2ParentId, setCurrentL2ParentId] = useState(null);

    const dispatch = useDispatch();

    const handleEditL1 = (rowData) => {
        setShowL1Dialog(true);
        setL1Editable(true);
        setL1Viewable(false);
        setL1RowRecord(rowData);
    };

    const onHideL1 = () => {
        setShowL1Dialog(false);
        getLevel1Questions();
    };

    const onCloseL1 = () => {
        setShowL1Dialog(false);
    };

    // Handle L2 Question Actions
    const handleAddL2 = (parentRow) => {
        // parentRow is the Level 1 record
        setCurrentL1Parent(parentRow._id);
        setCurrentL1ParentId(parentRow.id);
        setShowL2Dialog(true);
        setL2Editable(false);
        setL2Viewable(false);
        setL2RowRecord(null);
    };

    const handleEditL2 = (rowData, parentRow) => {
        setCurrentL1Parent(parentRow._id);
        setCurrentL1ParentId(parentRow.id);
        setShowL2Dialog(true);
        setL2Editable(true);
        setL2Viewable(false);
        setL2RowRecord(rowData);
    };

    const onHideL2 = () => {
        setShowL2Dialog(false);
        if (currentL1Parent) {
            getLevel2Questions(currentL1Parent);
        }
    };

    const onCloseL2 = () => {
        setShowL2Dialog(false);
    };

    // Handle L3 Question Actions
    const handleAddL3 = (parentRow) => {
        // parentRow is the Level 2 record
        setCurrentL2Parent(parentRow._id);
        setCurrentL2ParentId(parentRow.id);
        setShowL3Dialog(true);
        setL3Editable(false);
        setL3Viewable(false);
        setL3RowRecord(null);
    };

    const handleEditL3 = (rowData, parentRow) => {
        setCurrentL2Parent(parentRow._id);
        setCurrentL2ParentId(parentRow.id);
        setShowL3Dialog(true);
        setL3Editable(true);
        setL3Viewable(false);
        setL3RowRecord(rowData);
    };

    const onHideL3 = () => {
        setShowL3Dialog(false);
        if (currentL2Parent) {
            getLevel3Questions(currentL2Parent);
        }
    };

    const onCloseL3 = () => {
        setShowL3Dialog(false);
    };

    // Handle Status Switch (only for Level 2 and Level 3)
    const handleInputSwitch = async () => {
        if (!switchData || !switchLevel) return;

        const newStatus = !switchData.active;
        const payload = { active: newStatus, id: switchData._id };
        let response = null;

        if (switchLevel === 'L2') {
            response = await dispatch(handlePatchRequest(payload, endPoints.updateStatusLevel2Question, false, true));
            if (response?.status === 200) {
                // Refresh Level 2 list for this parent
                if (switchData.parent) {
                    await getLevel2Questions(switchData.parent);
                }
            }
        } else if (switchLevel === 'L3') {
            response = await dispatch(handlePatchRequest(payload, endPoints.updateStatusLevel3Question, false, true));
            if (response?.status === 200) {
                // Refresh Level 3 list for this parent
                if (switchData.parent) {
                    await getLevel3Questions(switchData.parent);
                }
            }
        }

        handleSwitchModalClose();
    };

    const handleSwitchModalOpen = (rowData, level) => {
        setSwitchModal(true);
        setSwitchData(rowData);
        setSwitchLevel(level);
    };

    const handleSwitchModalClose = () => {
        setSwitchModal(false);
        setSwitchData(null);
        setSwitchLevel(null);
    };

    // Fetch Data
    const getLevel1Questions = async () => {
        const response = await handleGetRequest(endPoints.getAllLevel1Questions, true, false, dispatch);
        if (response?.data) {
            const questions = response.data;
            setLevel1Questions(questions);
            setExpandedRows({});
        } else {
            setLevel1Questions([]);
            setExpandedRows({});
        }
    };

    const getLevel2Questions = async (parentId) => {
        const params = new URLSearchParams({
            parent: parentId
        });
        const response = await handleGetRequest(endPoints.getAllLevel2Questions(params.toString()), true, false, dispatch);
        if (response?.data) {
            setLevel2Questions((prev) => ({
                ...prev,
                [parentId]: response.data
            }));
        }
    };

    const getLevel3Questions = async (parentId) => {
        const params = new URLSearchParams({
            parent: parentId
        });
        const response = await handleGetRequest(endPoints.getAllLevel3Questions(params.toString()), true, false, dispatch);
        if (response?.data) {
            setLevel3Questions((prev) => ({
                ...prev,
                [parentId]: response.data
            }));
        }
    };

    // Row Expansion Handlers
    const onRowExpand = (event) => {
        const rowData = event.data;
        const rowId = rowData._id; // use Mongo _id as requested
        setExpandedRows((prev) => ({
            ...prev,
            [rowId]: true
        }));

        // Always load Level 2 questions when a Level 1 row is expanded
        getLevel2Questions(rowId);
    };

    const onRowCollapse = (event) => {
        const rowData = event.data;
        const rowId = rowData._id;
        setExpandedRows((prev) => {
            const newState = { ...prev };
            delete newState[rowId];
            return newState;
        });
    };

    const onL2RowExpand = (event) => {
        const rowData = event.data;
        const rowId = rowData._id; // use Mongo _id as requested
        setExpandedL2Rows((prev) => ({
            ...prev,
            [rowId]: true
        }));

        // Load L3 questions when L2 row is expanded
        if (rowData.hasMenu) {
            getLevel3Questions(rowId);
        }
    };

    const onL2RowCollapse = (event) => {
        const rowData = event.data;
        const rowId = rowData._id;
        setExpandedL2Rows((prev) => {
            const newState = { ...prev };
            delete newState[rowId];
            return newState;
        });
    };

    // Check if row is expandable
    const isRowExpandable = () => {
        // All Level 1 questions should be expandable
        return true;
    };

    const statusBodyTemplate = (rowData) => {
        const isActive = rowData.active;

        return (
            <div className="flex justify-content-center">
                <Tag
                    value={isActive ? 'Active' : 'Inactive'}
                    severity={isActive ? 'success' : 'danger'}
                    className="px-3 py-1 text-xs border-round-lg"
                />
            </div>
        );
    };

    const l1ActionBodyTemplate = (rowData) => {
        return (
            <div className="actions">
                <Button tooltip="Edit" icon="pi pi-pencil" tooltipOptions={{ position: 'top' }} onClick={() => handleEditL1(rowData)} className="p-button-rounded p-button-success mr-2 ml-2" />
            </div>
        );
    };

    const l2ActionBodyTemplate = (rowData, l1ParentRow) => {
        return (
            <div className="actions">
                <InputSwitch tooltip="Status" tooltipOptions={{ position: 'top' }} checked={rowData.active} onChange={() => handleSwitchModalOpen(rowData, 'L2')} />
                <Button tooltip="Edit" icon="pi pi-pencil" tooltipOptions={{ position: 'top' }} onClick={() => handleEditL2(rowData, l1ParentRow)} className="p-button-rounded p-button-success mr-2 ml-2" />
            </div>
        );
    };

    const l3ActionBodyTemplate = (rowData, l2ParentRow) => {
        return (
            <div className="actions">
                <InputSwitch tooltip="Status" tooltipOptions={{ position: 'top' }} checked={rowData.active} onChange={() => handleSwitchModalOpen(rowData, 'L3')} />
                <Button tooltip="Edit" icon="pi pi-pencil" tooltipOptions={{ position: 'top' }} onClick={() => handleEditL3(rowData, l2ParentRow)} className="p-button-rounded p-button-success mr-2 ml-2" />
            </div>
        );
    };

    // Row expansion content for Level 2 (shows Level 3 questions directly under the L2 row)
    const l2RowExpansionTemplate = (l2RowData) => {
        const l2Id = l2RowData._id;
        const l3Questions = level3Questions[l2Id] || [];

        return (
            <div className="p-3" style={{ marginLeft: '2rem' }}>
                <div className="flex justify-content-between align-items-center mb-3">
                    <h6>Level 3 Questions</h6>
                    <Button
                        label="Add L3 Question"
                        icon="pi pi-plus"
                        className="p-button-success p-button-sm"
                        tooltip="Add a new Level 3 question"
                        tooltipOptions={{ position: 'top' }}
                        onClick={() => handleAddL3(l2RowData)}
                    />
                </div>
                <DataTable value={l3Questions} className="p-datatable-sm" emptyMessage="No Level 3 questions found.">
                    <Column field="id" header="Sequence ID" />
                    <Column field="text" header="Question level Three" />
                    <Column field="active" header="Status" body={statusBodyTemplate} />
                    <Column header="Action" body={(rowData) => l3ActionBodyTemplate(rowData, l2RowData)} className="Table__ActionHeading__CentredAlign" />
                </DataTable>
            </div>
        );
    };

    // Row expansion content for Level 1 (shows Level 2 questions)
    const rowExpansionTemplate = (rowData) => {
        const l1MongoId = rowData._id;
        const l2Questions = level2Questions[l1MongoId] || [];

        return (
            <div className="p-4" style={{ marginLeft: '2rem' }}>
                <div className="flex justify-content-between align-items-center mb-3">
                    <h5>Level 2 Questions</h5>
                    <Button
                        label="Add L2 Question"
                        icon="pi pi-plus"
                        className="p-button-success p-button-sm"
                        tooltip="Add a new Level 2 question"
                        tooltipOptions={{ position: 'top' }}
                        onClick={() => handleAddL2(rowData)}
                    />
                </div>
                <DataTable
                    value={l2Questions}
                    className="p-datatable-sm"
                    emptyMessage="No Level 2 questions found."
                    dataKey="_id"
                    expandedRows={expandedL2Rows}
                    onRowExpand={onL2RowExpand}
                    onRowCollapse={onL2RowCollapse}
                    rowExpansionTemplate={l2RowExpansionTemplate}
                >
                    <Column expander={(childRow) => childRow.hasMenu} style={{ width: '3rem' }} />
                    <Column field="id" header="Sequence ID" />
                    <Column field="text" header="Question level Two" />
                    <Column field="active" header="Status" body={statusBodyTemplate} />
                    <Column header="Action" body={(childRow) => l2ActionBodyTemplate(childRow, rowData)} className="Table__ActionHeading__CentredAlign" />
                </DataTable>
            </div>
        );
    };

    useEffect(() => {
        getLevel1Questions();
    }, []);

    return (
        <div className="p-4">
            {/* Level 1 Dialog */}
            <Dialog
                header={<div>{l1Editable ? 'Edit L1 Question' : l1Viewable ? 'View L1 Question' : 'Add L1 Question'}</div>}
                visible={showL1Dialog}
                onHide={onCloseL1}
                breakpoints={{ '960px': '75vw', '640px': '100vw' }}
                style={{ width: '40vw' }}
                draggable={false}
            >
                <EditLevel1Question rowRecord={l1RowRecord} editable={l1Editable} viewable={l1Viewable} onHide={onHideL1} onClose={onCloseL1} />
            </Dialog>

            {/* Level 2 Dialog */}
            <Dialog
                header={<div>{l2Editable ? 'Edit L2 Question' : l2Viewable ? 'View L2 Question' : 'Add L2 Question'}</div>}
                visible={showL2Dialog}
                onHide={onCloseL2}
                breakpoints={{ '960px': '75vw', '640px': '100vw' }}
                style={{ width: '40vw' }}
                draggable={false}
            >
                <AddEditLevel2Question rowRecord={l2RowRecord} editable={l2Editable} viewable={l2Viewable} onHide={onHideL2} onClose={onCloseL2} parentMongoId={currentL1Parent} parentId={currentL1ParentId} />
            </Dialog>

            {/* Level 3 Dialog */}
            <Dialog
                header={<div>{l3Editable ? 'Edit L3 Question' : l3Viewable ? 'View L3 Question' : 'Add L3 Question'}</div>}
                visible={showL3Dialog}
                onHide={onCloseL3}
                breakpoints={{ '960px': '75vw', '640px': '100vw' }}
                style={{ width: '40vw' }}
                draggable={false}
            >
                <AddEditLevel3Question rowRecord={l3RowRecord} editable={l3Editable} viewable={l3Viewable} onHide={onHideL3} onClose={onCloseL3} parentMongoId={currentL2Parent} parentId={currentL2ParentId} />
            </Dialog>

            <ActiveInactiveDialog visible={switchModal} onHide={handleSwitchModalClose} onConfirm={handleInputSwitch} onCancel={handleSwitchModalClose} status={switchData?.active} />

            <div className="flex justify-content-between align-items-center mb-3">
                <h3>Questions</h3>
            </div>

            <DataTable
                value={level1Questions}
                expandedRows={expandedRows}
                onRowExpand={onRowExpand}
                onRowCollapse={onRowCollapse}
                rowExpansionTemplate={rowExpansionTemplate}
                dataKey="_id"
                className="p-datatable-sm datatable-responsive"
                emptyMessage="No Level 1 questions found."
            >
                <Column expander={isRowExpandable} style={{ width: '3rem' }} />
                <Column field="id" header="Sequence Id" />
                <Column field="text" header="Question" />
                <Column field="active" header="Status" body={statusBodyTemplate} />
                <Column field="team?.name" header="Assigned To Team" body={(rowData) => rowData.team?.name || 'N/A'} />
                <Column header="Action" body={l1ActionBodyTemplate} className="Table__ActionHeading__CentredAlign" />
            </DataTable>
        </div>
    );
};

export default Questions;
